import { supabase } from "@/lib/supabase";
import type { ProfitBucket, ProfitPeriod, ProfitProductRow, ProfitSummary } from "@/types";

interface SaleRow {
  id: string;
  total: number | null;
  cost_total: number | null;
  created_at: string;
  branch_id: string | null;
}

interface SaleItemRow {
  sale_id: string | null;
  product_id: string | null;
  name: string | null;
  quantity: number | null;
  unit_price: number | null;
  discount: number | null;
  cost_price: number | null;
  line_total: number | null;
  item_type: string | null;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface PeriodRange {
  from: Date;
  to: Date;
}

/** Builds the date range used for each reporting period. */
export function getPeriodRange(period: ProfitPeriod): PeriodRange {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  from.setHours(0, 0, 0, 0);

  if (period === "day") {
    from.setDate(from.getDate() - 29);
  } else if (period === "week") {
    const day = from.getDay();
    const diff = (day + 6) % 7; // Monday-based
    from.setDate(from.getDate() - diff);
    from.setDate(from.getDate() - 7 * 11);
  } else if (period === "month") {
    from.setDate(1);
    from.setMonth(from.getMonth() - 11);
  } else {
    from.setMonth(0, 1);
    from.setFullYear(from.getFullYear() - 4);
  }
  return { from, to };
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function weekKey(date: Date): string {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  const day = copy.getDay();
  const diff = (day + 6) % 7;
  copy.setDate(copy.getDate() - diff);
  return dayKey(copy);
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

function bucketKey(date: Date, period: ProfitPeriod): string {
  if (period === "week") return weekKey(date);
  if (period === "month") return monthKey(date);
  if (period === "year") return String(date.getFullYear());
  return dayKey(date);
}

const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function bucketLabel(key: string, period: ProfitPeriod): string {
  if (period === "year") return key;
  if (period === "month") {
    const [year, month] = key.split("-");
    return `${MONTHS_SHORT[Number(month) - 1]} ${year.slice(2)}`;
  }
  const date = new Date(`${key}T00:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  return `${pad(date.getDate())} ${MONTHS_SHORT[date.getMonth()]}`;
}

/** Generates every bucket key in the range so the chart shows continuous time. */
function buildBucketKeys(range: PeriodRange, period: ProfitPeriod): string[] {
  const keys: string[] = [];
  const cursor = new Date(range.from);
  cursor.setHours(0, 0, 0, 0);
  const guard = period === "day" ? 400 : period === "week" ? 80 : period === "month" ? 40 : 12;
  let count = 0;
  while (cursor <= range.to && count < guard) {
    const key = bucketKey(cursor, period);
    if (!keys.includes(key)) keys.push(key);
    if (period === "day") cursor.setDate(cursor.getDate() + 1);
    else if (period === "week") cursor.setDate(cursor.getDate() + 7);
    else if (period === "month") cursor.setMonth(cursor.getMonth() + 1);
    else cursor.setFullYear(cursor.getFullYear() + 1);
    count += 1;
  }
  return keys;
}

export interface ProfitResult {
  summary: ProfitSummary | null;
  error: string | null;
}

const EMPTY_SUMMARY: ProfitSummary = {
  revenue: 0,
  cost: 0,
  profit: 0,
  margin: 0,
  salesCount: 0,
  avgTicket: 0,
  unitsSold: 0,
  buckets: [],
  topProducts: [],
  products: [],
};

export async function loadProfitData(
  period: ProfitPeriod,
  branchId: string | null,
): Promise<ProfitResult> {
  try {
    const range = getPeriodRange(period);

    let salesQuery = supabase
      .from("sales")
      .select("id, total, cost_total, created_at, branch_id")
      .eq("status", "paid")
      .gte("created_at", range.from.toISOString())
      .lte("created_at", range.to.toISOString())
      .order("created_at", { ascending: true })
      .limit(5000);
    if (branchId) salesQuery = salesQuery.eq("branch_id", branchId);

    const { data: salesData, error: salesError } = await salesQuery;
    if (salesError) {
      return { summary: null, error: "No se pudieron cargar las ventas." };
    }
    const sales = (salesData ?? []) as SaleRow[];
    if (sales.length === 0) {
      return {
        summary: { ...EMPTY_SUMMARY, buckets: buildBucketKeys(range, period).map((key) => ({ key, label: bucketLabel(key, period), revenue: 0, cost: 0, profit: 0, salesCount: 0 })) },
        error: null,
      };
    }

    const saleIds = sales.map((s) => s.id);
    const { data: itemsData, error: itemsError } = await supabase
      .from("sale_items")
      .select("sale_id, product_id, name, quantity, unit_price, discount, cost_price, line_total, item_type")
      .in("sale_id", saleIds);
    if (itemsError) {
      return { summary: null, error: "No se pudieron cargar los detalles de venta." };
    }
    const items = (itemsData ?? []) as SaleItemRow[];

    const bucketMap = new Map<string, ProfitBucket>();
    buildBucketKeys(range, period).forEach((key) => {
      bucketMap.set(key, { key, label: bucketLabel(key, period), revenue: 0, cost: 0, profit: 0, salesCount: 0 });
    });

    let totalRevenue = 0;
    let totalCost = 0;

    sales.forEach((sale) => {
      const key = bucketKey(new Date(sale.created_at), period);
      const bucket = bucketMap.get(key);
      if (!bucket) return;
      const revenue = Number(sale.total ?? 0);
      const cost = Number(sale.cost_total ?? 0);
      bucket.revenue = round2(bucket.revenue + revenue);
      bucket.cost = round2(bucket.cost + cost);
      bucket.profit = round2(bucket.profit + (revenue - cost));
      bucket.salesCount += 1;
      totalRevenue += revenue;
      totalCost += cost;
    });

    const productMap = new Map<string, ProfitProductRow>();
    let unitsSold = 0;
    items.forEach((item) => {
      if (item.item_type === "service") return;
      const qty = Number(item.quantity ?? 0);
      const gross = Number(item.line_total ?? (Number(item.unit_price ?? 0) * qty - Number(item.discount ?? 0)));
      const cost = Number(item.cost_price ?? 0) * qty;
      unitsSold += qty;
      const key = item.product_id ?? `name:${item.name ?? "Producto"}`;
      const existing = productMap.get(key);
      if (existing) {
        existing.quantity = round2(existing.quantity + qty);
        existing.revenue = round2(existing.revenue + gross);
        existing.cost = round2(existing.cost + cost);
        existing.profit = round2(existing.profit + (gross - cost));
      } else {
        productMap.set(key, {
          product_id: item.product_id,
          name: item.name ?? "Producto",
          quantity: qty,
          revenue: round2(gross),
          cost: round2(cost),
          profit: round2(gross - cost),
        });
      }
    });

    const products = Array.from(productMap.values()).sort((a, b) => b.profit - a.profit);
    const profit = round2(totalRevenue - totalCost);
    const summary: ProfitSummary = {
      revenue: round2(totalRevenue),
      cost: round2(totalCost),
      profit,
      margin: totalRevenue > 0 ? round2((profit / totalRevenue) * 100) : 0,
      salesCount: sales.length,
      avgTicket: sales.length > 0 ? round2(totalRevenue / sales.length) : 0,
      unitsSold: round2(unitsSold),
      buckets: Array.from(bucketMap.values()),
      topProducts: products.slice(0, 8),
      products,
    };

    return { summary, error: null };
  } catch {
    return { summary: null, error: "Ocurrió un error inesperado al calcular las ganancias." };
  }
}